import {
  PRIME_TRANSPORT,
  decryptWithKeyHex,
  deriveAesKeyFromECDH,
  encryptWithKeyHex,
  isEncryptedPayload,
  type E2eeInnerCall,
} from "@prime-assistant/shared/e2ee";
import { getApiBase } from "./api-base";

const CACHE_MS = 60 * 60 * 1000;
let publicKeyCache: { key: string; expiresAt: number } | null = null;

export function clearPrimeE2eeCache(): void {
  publicKeyCache = null;
}

async function fetchServerPublicKey(): Promise<string> {
  if (publicKeyCache && publicKeyCache.expiresAt > Date.now()) {
    return publicKeyCache.key;
  }
  const response = await fetch(`${getApiBase()}/e2ee/public-key`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  const payload = (await response.json().catch(() => ({}))) as {
    success?: boolean;
    serverPublicKey?: string;
    error?: string;
  };
  if (!response.ok || !payload.serverPublicKey) {
    throw new Error(payload.error ?? "Could not load the secure transport key");
  }
  publicKeyCache = {
    key: payload.serverPublicKey,
    expiresAt: Date.now() + CACHE_MS,
  };
  return publicKeyCache.key;
}

export async function invokeEncryptedApi(
  path: string,
  init: RequestInit,
  headers: Record<string, string>,
): Promise<{ status: number; payload: unknown }> {
  const method = (init.method ?? "GET").toUpperCase();
  const url = new URL(path, "http://prime.local");
  const inner: E2eeInnerCall = {
    method,
    path: url.pathname,
    query: url.search || undefined,
  };
  if (method !== "GET" && method !== "HEAD" && typeof init.body === "string" && init.body) {
    inner.body = JSON.parse(init.body) as unknown;
  }
  const serverPublicKey = await fetchServerPublicKey();
  const { clientPublicKey, aesKeyHex } = await deriveAesKeyFromECDH(serverPublicKey);
  const encrypted = await encryptWithKeyHex(JSON.stringify(inner), aesKeyHex);
  const response = await fetch(`${getApiBase()}/e2ee/invoke`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-prime-transport": PRIME_TRANSPORT,
      "x-encrypt-response": "1",
      ...headers,
    },
    body: JSON.stringify({ clientPublicKey, encrypted }),
    signal: init.signal ?? undefined,
  });
  const raw = (await response.json().catch(() => ({}))) as unknown;
  if (!isEncryptedPayload(raw)) {
    const plain = raw as { error?: string };
    if (typeof plain.error === "string" && plain.error.trim()) {
      throw new Error(plain.error);
    }
    throw new Error(
      response.status
        ? `Secure request failed (${response.status})`
        : "Secure request failed",
    );
  }
  const payload = JSON.parse(await decryptWithKeyHex(raw, aesKeyHex)) as unknown;
  return { status: response.status, payload };
}
